import { useEffect, useState, useRef } from "react";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import RawMaterialService from "../../../services/RawMaterials/RawMaterialService";

interface RMCategoryDatatableProps {
  categories: any[];
  totalRecords: number;
  pageNo: number;
  pageSize: number;
  loading: boolean;
  onPageChange: (event: any) => void;
  onUpdated: () => void;
}

const RMCategoryDatatable: React.FC<RMCategoryDatatableProps> = ({
  categories,
  totalRecords,
  pageNo,
  pageSize,
  loading,
  onPageChange,
  onUpdated,
}) => {
  const [localCategories, setLocalCategories] = useState<any[]>([]);
  const toast = useRef<Toast>(null);

  useEffect(() => {
    setLocalCategories(categories || []);
  }, [categories]);

  const showMessage = (label: string, message: string, severity: any) => {
    toast.current?.show({ severity, summary: label, detail: message, life: 3000 });
  };

  const textEditor = (options: any) => (
    <InputText
      value={options.value}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        options.editorCallback!(e.target.value)
      }
      style={{ width: "95%" }}
    />
  );

  const onRowEditComplete = async (event: DataTableRowEditCompleteEvent) => {
    const { newData, index } = event;
    const updated = [...localCategories];
    updated[index] = newData;
    setLocalCategories(updated);

    try {
      const response = await RawMaterialService.updateRawMaterialCategory({
        CategoryId: newData.categoryId,
        CategoryName: newData.categoryName,
        Description: newData.description || "",
      });

      if (response?.isSuccess) {
        showMessage("Update Category", "Updated successfully!", "success");
      } else {
        const errorMsg = response?.message || "Failed to update";
        showMessage("Update Category", errorMsg, "error");
      }
    } catch (error: any) {
      console.error("Error updating category:", error);
      const errorMsg = error?.response?.data?.message || "Error occurred";
      showMessage("Update Category", errorMsg, "error");
    } finally {
      onUpdated();
    }
  };

  const confirmDelete = (rowData: any) => {
    confirmDialog({
      message: "Are you sure you want to delete this category?",
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => handleDelete(rowData),
    });
  };

  const handleDelete = async (rowData: any) => {
    try {
      const response = await RawMaterialService.deleteRMcategory(rowData.categoryId);
      if (response) {
        setLocalCategories((prev) =>
          prev.filter((c) => c.categoryId !== rowData.categoryId)
        );
        showMessage("Delete Category", "Deleted successfully!", "success");
        onUpdated();
      } else {
        showMessage("Delete Category", "Failed to delete", "error");
      }
    } catch (error) {
      console.error("Error deleting category:", error);
      showMessage("Delete Category", "Error occurred", "error");
    }
  };

  return (
    <div className="col-12">
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="card border-noround p-2">
        <DataTable
          value={localCategories}
          paginator
          lazy
          first={(pageNo - 1) * pageSize}
          rows={pageSize}
          totalRecords={totalRecords}
          onPage={onPageChange}
          stripedRows
          editMode="row"
          loading={loading}
          onRowEditComplete={onRowEditComplete}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          // paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink  NextPageLink LastPageLink CurrentPageReport"
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"

          currentPageReportTemplate="{first} to {last} of {totalRecords}"
          sortMode="multiple"
        >
          <Column field="categoryName" header="Category Name" editor={textEditor}  />
          <Column field="description" header="Description" editor={textEditor}  />
          <Column header="Action" rowEditor style={{ width: "8%" }} />
          <Column
            header="Delete"
            body={(rowData) => (
              <Button
                icon="pi pi-trash"
                onClick={() => confirmDelete(rowData)}
                className="p-button-rounded p-button-text p-button-danger"
              />
            )}
          />
        </DataTable>
      </div>
    </div>
  );
};

export default RMCategoryDatatable;
